/**
 * Apply topic-highlight state to transcript paragraph/block DOM nodes via
 * direct class + CSS-variable mutation. Bypasses React re-renders so prev/next
 * topic-match navigation and topic selection don't reconcile the (large)
 * transcript tree.
 *
 * Looks up every `[data-paragraph-id]` element inside `container`, then for
 * each one:
 *   - toggles `.topic-highlighted` when its paragraph_id is in the
 *     highlighted set
 *   - toggles `.topic-active` when its paragraph_id matches the active id
 *   - sets `--topic-highlight-color` from `paragraphHighlightColors` when
 *     present (the CSS rule falls back to `var(--badge-blue-text)` otherwise)
 *
 * Pass either the scrollable container (used by TranscriptionText.tsx) or a
 * local wrapper ref (used by _TranscriptionDiff.tsx) — anything that bounds
 * the relevant transcript DOM. Scoping to a local container is important when
 * multiple Transcript instances exist on the page (see CLAUDE.md
 * "Duplicate Transcript Instances").
 */
export function adjustDOMClassesToTopicHighlight(
  container: HTMLElement | null,
  highlightedParagraphIds: ReadonlySet<string> | undefined,
  activeParagraphId: string | undefined,
  paragraphHighlightColors: ReadonlyMap<string, string> | undefined,
): void {
  if (!container) return;
  const paragraphs = container.querySelectorAll<HTMLElement>(
    '[data-paragraph-id]',
  );
  paragraphs.forEach(p => {
    const pid = p.getAttribute('data-paragraph-id');
    if (!pid) {
      p.classList.remove('topic-highlighted', 'topic-active');
      p.style.removeProperty('--topic-highlight-color');
      return;
    }
    const isHighlighted = highlightedParagraphIds?.has(pid) ?? false;
    const isActive = pid === activeParagraphId;
    p.classList.toggle('topic-highlighted', isHighlighted);
    p.classList.toggle('topic-active', isActive);
    const color = paragraphHighlightColors?.get(pid);
    if (color) {
      p.style.setProperty('--topic-highlight-color', color);
    } else {
      p.style.removeProperty('--topic-highlight-color');
    }
  });
}
